import { V1SMOptions, V1SMSeries, V1SMQueryItem } from '@/lib/api/lib'
import { smApiClient } from '@/lib/api/lib/client'
import { VISIT } from '@/lib/visit'
import { DAY_MS, getNowUTC } from '@/lib/date'

/** Days of training history the heatmaps load: they draw four months. */
export const HISTORY_DAYS = 120

export function sumItemValues(item: V1SMQueryItem): number {
    return Object.values(item.values || {}).reduce(
        (sum, value) => sum + value,
        0
    )
}

type QueryEvents = Parameters<typeof smApiClient.queryEvents>[0]

/**
 * One total per group over a window ("1d", "7d", "30d", "90d") ending at
 * `to` (default now). The server turns the window into a single point, so
 * totals are never counted twice.
 */
export async function queryWindow(
    query: Omit<QueryEvents, 'from' | 'step'> & { window: string }
): Promise<V1SMQueryItem[]> {
    const data = await smApiClient.queryEvents(query)
    return data?.items ?? []
}

/**
 * Totals per value of `label`, highest first; rows sharing a value (grouped
 * by more labels) add up, and zero totals are left out.
 */
export function totalsBy(
    items: V1SMQueryItem[],
    label: string
): { id: string; total: number }[] {
    const totals = new Map<string, number>()
    for (const item of items) {
        const id = item.labels?.[label]
        if (id) totals.set(id, (totals.get(id) || 0) + sumItemValues(item))
    }
    return [...totals]
        .filter(([, total]) => total > 0)
        .map(([id, total]) => ({ id, total }))
        .sort((a, b) => b.total - a.total)
}

/**
 * Workouts done in the window, per value of `sumBy` (userId, pId...);
 * only one user's with userId.
 */
export function workoutsDone(window: string, sumBy: string, userId?: string) {
    return queryWindow({
        series: V1SMSeries.V1WorkoutHistory,
        operation: V1SMOptions.WorkoutComplete,
        userId,
        window,
        sumBy
    })
}

/**
 * Plan requests coaches received in the window, per coach (userId): the
 * coaches' trending stat, also their leaderboard and profile Requests.
 */
export function requestsReceived(window: string, coachId?: string) {
    return queryWindow({
        series: V1SMSeries.V1CommunityWorkoutPlanRequest,
        operation: V1SMOptions.Received,
        userId: coachId,
        window,
        sumBy: 'userId'
    })
}

/**
 * Days each user opened the app in the window, per user. Being there makes
 * someone active in the window; nothing is ranked on it.
 */
export function visitors(window: string) {
    return queryWindow({
        ...VISIT,
        window,
        sumBy: 'userId'
    })
}

/** Values of `label` ordered by their total, highest first. */
export function rankByTotal(items: V1SMQueryItem[], label: string): string[] {
    return totalsBy(items, label).map((e) => e.id)
}

/** Records in the order of ids (a ranking); records not in ids are dropped. */
export function inRankOrder<T>(
    records: T[],
    ids: string[],
    key: (record: T) => string | undefined
): T[] {
    const at = new Map(ids.map((id, i) => [id, i]))
    return records
        .filter((r) => at.has(key(r) || ''))
        .sort((a, b) => at.get(key(a) || '')! - at.get(key(b) || '')!)
}

/**
 * Records in the order of ids, then the records not in ids in the order
 * they came: a short ranking padded with everyone else.
 */
export function rankedFirst<T>(
    records: T[],
    ids: string[],
    key: (record: T) => string | undefined
): T[] {
    const ranked = new Set(ids)
    return [
        ...inRankOrder(records, ids, key),
        ...records.filter((r) => !ranked.has(key(r) || ''))
    ]
}

// Records a ranked list reads at once: the filter holds the ranked ids, so
// this bounds a ranking, not a collection.
const RANKED_FETCH = 500

/**
 * Every match of a ranked filter, in rank order (records the ranking misses
 * after the ranked ones). Cache this once per filter and let the screen
 * show it a page at a time: page 1 holds the best whatever order the
 * backend keeps, and a load-more reads nothing. A filter on the ranked ids
 * bounds the read.
 */
export async function rankAll<T>(
    fetchPage: (
        page: number,
        perPage: number
    ) => Promise<{ items: T[]; totalPages: number }>,
    order: string[],
    key: (record: T) => string | undefined
): Promise<T[]> {
    const first = await fetchPage(1, RANKED_FETCH)
    const rest = await Promise.all(
        Array.from({ length: first.totalPages - 1 }, (_, i) =>
            fetchPage(i + 2, RANKED_FETCH)
        )
    )
    return rankedFirst(
        [first, ...rest].flatMap((p) => p.items),
        order,
        key
    )
}

/** Ratings at this complexity level or harder (levels run 1-5). */
export const HARD_LEVEL = 4

/**
 * Programmes ranked by their ratings at `level` or harder in the window,
 * most rated first: "most complex" means rated hard by the people who did
 * it, not just rated.
 */
export async function complexProgrammeIds(
    window: string,
    level: number
): Promise<string[]> {
    const items = await queryWindow({
        series: V1SMSeries.V1ProgrammeExcerciseComplexity,
        operation: V1SMOptions.Rate,
        window,
        sumBy: 'pId,complexityKind'
    })
    return rankByTotal(
        items.filter((i) => Number(i.labels?.complexityKind) >= level),
        'pId'
    )
}

/** A user's completed workouts per day over the last HISTORY_DAYS. */
export async function loadWorkoutHistory(userId: string) {
    const to = getNowUTC()
    const data = await smApiClient.queryEvents({
        series: V1SMSeries.V1WorkoutHistory,
        operation: V1SMOptions.WorkoutComplete,
        from: to - HISTORY_DAYS * DAY_MS,
        to,
        userId,
        step: '1d',
        sumBy: 'operation'
    })
    return data?.items ?? []
}
